<template>
  <div class="bg-gray-800 border border-gray-600  "
       :class="{ 'host--highlighted': highlighted }">
    <div class="flex flex-col sm:flex-row sm:items-center justify-between mt-4">
      <div class="flex flex-col justify-between min-w-0">
        <div class="flex items-center sm:mr-8">
          <h2 class="relative text-2xl  mr-3">
            <div class="glow-block "
                 :class="[
                     {'glow-block--yellow': findingsCount},
                     {'glow-block--green': !findingsCount}
                   ]"></div>
            <span class="ml-4">{{ ip }}</span>
          </h2>
          <div class="flex uppercase text-xs px-1 font-semibold tracking-wider border
                      leading-relaxed "
               :class="[
                 {'bg-gray-600 text-gray-200 border-gray-600': category === 'external'},
                 // eslint-disable-next-line max-len
                 {'bg-yellow-700 text-gray-200 border-yellow-500 shadow-glowyellow': category === 'internal'},
               ]">
            <span class="block">{{ category }}</span>
          </div>
        </div>

        <div class="ml-4 mt-2 md:mt-0 md:mr-8 ">
          <span class="flag-icon"
                :title="country.toLowerCase()"
                :aria-label="country.toLowerCase()"
                :class="`flag-icon-${country.toLowerCase()}`"
                @mouseover="showTooltip = true" @mouseout="showTooltip = false">
            <span v-if="showTooltip" class="tooltip">
              {{ country }}
            </span>
          </span>
        </div>
        <div class="ml-4 mt-2 lg:mt-0 lg:mr-8">
          <span v-if="asn">
            {{ asn }}
          </span>
          <span v-else
                class="italic text-gray-300">
            No ASN
          </span>
        </div>
        <div class="ml-4 mt-2 lg:mt-0 lg:mr-8">
          <span v-if="org">
            {{ org }}
          </span>
          <span v-else
                class="italic text-gray-300">
            No Organization
          </span>
        </div>
      </div>
      <div class="flex ml-4 mt-4
                  sm:justify-end sm:ml-0 sm:mt-0 sm:pr-8 flex-shrink-0 ">
        <WarningIcon v-if="findingsCount" class="inline-block mr-1" />
        <span
            class=" "
            :class="[
                {'italic text-gray-300': findingsCount === 0},
              ]">
            {{ findingsCount }} {{ 'Finding' | pluralize(findingsCount) }}
        </span>
      </div>
    </div>
    <div
      v-if="isSuperuser"
      class="block mb-2 pl-5 ">
      <Checkbox
        :id="`${ip}-dir-enum`"
        data-testid="host-checkbox"
        :checked="targetsForDictEnum"
        @change="() => $emit('dirEnumTargetUpdated',  ip)"
        label="Select for directory enumeration"
        :value="ip"/>
    </div>
    <template v-if="expanded">
      <div
          v-if="categoryExplanation"
          class="border-t border-gray-600 pt-2 px-2">
        <p class="text-sm mb-2 bg-gray-900 text-gray-200 border border-gray-600 p-2">
          <pre class="break-all whitespace-pre-wrap">{{ categoryExplanation }}</pre>
        </p>
      </div>
      <div v-if="findingsCount" class="border-t border-gray-600 p-2">
        <div class="relative bg-gray-900 pb-2 pt-3
             ">
          <div class="warning-strip "></div>
          <div class="flex mb-2">
            <WarningIcon class="mx-2" />
            <span class="uppercase relative inline-block">
              Findings [{{ findingsCount }}]
            </span>
          </div>
          <ul v-if="Object.keys(findings).length">
            <li v-for="(finding, index) in findings"
                class="yellow-block flex relative pl-3 mb-2 pr-2"
                :key="`finding-${index}`">
              <span>{{ finding }}</span>
            </li>
          </ul>
        </div>
      </div>
      <div class="border-t border-gray-600 p-2">
        <div class="flex flex-col mb-1">
            <span class="text-lg ">

              {{ 'Subdomain' | pluralize(subdomains.length) }} [{{ subdomains.length }}]
            </span>
        </div>
        <div
          v-if="subdomains.length > 0"
          class="flex flex-col lg:flex-row">
          <div
            :class="{ 'px-2' : screenshots.length > 0 }"
            class="lg:w-1/4 flex-grow ">
            <div
              class="uppercase text-gray-300 text-xs font-semibold tracking-widest mb-2">
              Subdomains
            </div>
            <ul class="  ">
              <li
                v-for="(subdomain, index) in subdomains"
                  :key="`${ip}-subdomain-${index}`"
              >
                <Checkbox
                  v-if="isSuperuser"
                  :id="`${ip}-${subdomain}_dict-enum`"
                  data-testid="subdomain-checkbox"
                  :checked="targetsForDictEnum"
                  @change="() => $emit('dirEnumTargetUpdated',  `${subdomain}.${rootDomain}`)"
                  :label="subdomain"
                  :value="`${subdomain}.${rootDomain}`"/>
                <div v-else>{{ subdomain }}</div>
              </li>
            </ul>
          </div>
          <div
            class='lg:w-3/4'
            v-if="screenshots.length > 0">
            <div class="uppercase text-gray-300 text-xs font-semibold tracking-widest mb-2">
              Screenshots
            </div>
            <ul
              class='grid grid-cols-2 gap-2 mb-2 lg:grid-cols-3 pr-2'>
              <li v-for="(screenshot, index) in screenshots"
                  class="flex flex-col uppercase px-1 pt-1 pb-2 bg-black font-semibold"
                  :key="`${ip}-subdomain-${index}`">
                <div
                  v-if="screenshot.file">
                  <a :href="screenshot.file"
                     target="_blank"
                     rel="noopener noreferrer">
                    <img :src="screenshot.file" loading="lazy"/>
                  </a>
                </div>
                <div class="text-xs text-gray-200 px-1 pt-2 truncate">
                  <CopySubdomain
                    :subdomain="screenshot.domainName"
                  />
                </div>
              </li>
            </ul>
          </div>
        </div>
        <p v-else class="italic text-gray-300 leading-5"
           >
          No subdomains found on this host.
        </p>
      </div>
      <div class="flex flex-col lg:flex-row border-t border-b border-gray-600 px-2"
           >
        <div class="lg:w-3/4 lg:border-r border-gray-600 pt-2 pb-4">
          <div class="flex flex-col mb-2">
            <span class="text-lg">
              {{ 'Port' | pluralize(displayPorts.length) }} [{{ displayPorts.length }}]
            </span>
          </div>
          <div v-if="screenshotTargets.length !== 0">
            <ul class="grid gap-2 grid-cols-3">
              <li v-for="(target, index) in screenshotTargets"
                  class="flex flex-col uppercase px-1 pt-1 pb-2 bg-black font-semibold mb-2"
                  :key="`${ip}-screenshot-target-${index}`">
                <div>
                  <a :href="target.primaryScreenshot.file"
                     :title="target.label"
                     target="_blank"
                     rel="noopener noreferrer"
                     class="block min-w-0 border border-gray-800">
                    <img :src="target.primaryScreenshot.file"
                         :alt="screenshotTargetAltText(target)"
                         class="w-full"
                         loading="lazy"/>
                  </a>
                  <button type="button"
                          class="screenshot-target-text text-xs leading-4 text-gray-100
                                 bg-gray-900 border-l border-r border-b border-gray-800
                                 px-1 py-1 normal-case"
                          @click="showTargetScreenshots(target)">
                    <div class="truncate">
                      {{ target.label }}
                    </div>
                    <div class="text-gray-300 truncate">
                      {{ 'Port' | pluralize(target.ports.length) }}:
                      {{ target.ports.join(', ') }}
                    </div>
                    <div v-if="additionalTargetScreenshotCount(target)"
                         class="text-blue-300 hover:text-blue-200">
                        [+{{ additionalTargetScreenshotCount(target) }} more screenshots]
                    </div>
                  </button>
                </div>
                <button type="button"
                        class="screenshot-target-text flex justify-between px-1 pt-1"
                        @click="showTargetScreenshots(target)">
                  <span class="text-white">{{ target.ports[0] }}</span>
                  <span class="flex items-center text-2xs text-gray-200">
                    <template v-if="target.state === 'open'">
                      <div class="rounded-full text-2xs bg-green-500 w-2 h-2 mr-1 shadow-glowgreen">
                      </div>
                    </template>
                    <template v-if="target.state === 'closed'">
                      <div
                        class="rounded-full text-2xs border border-gray-200 bg-black w-2 h-2 mr-1">
                      </div>
                    </template>
                    <div class="leading-3">{{ target.state }}</div>
                  </span>
                </button>
                <button type="button"
                        class="screenshot-target-text text-xs text-gray-200 px-1"
                        @click="showTargetScreenshots(target)">
                  {{ target.protocol }}
                  <template v-if="target.banner">
                    <span class="text-gray-600 mx-1">|</span>
                    <span class="truncate block">
                      {{ target.banner }}
                    </span>
                  </template>
                  <template v-else-if="target.product">
                    <span class="text-gray-600 mx-1">|</span>
                    {{ target.product }}
                  </template>
                </button>
              </li>
            </ul>
          </div>
          <div v-if="informationalPorts.length !== 0"
               class="mt-2">
            <ul class="grid gap-2 grid-cols-4">
              <li v-for="(port, index) in informationalPorts"
                  class="uppercase px-2 py-2 bg-black font-semibold mb-2 min-w-0"
                  :key="`${ip}-info-port-${index}`">
                <div class="flex justify-between">
                  <span class="text-white">{{ port.portid }}</span>
                  <span class="flex items-center text-2xs text-gray-200">
                    <template v-if="port.state === 'open'">
                      <div class="rounded-full text-2xs bg-green-500 w-2 h-2 mr-1 shadow-glowgreen">
                      </div>
                    </template>
                    <template v-if="port.state === 'closed'">
                      <div
                        class="rounded-full text-2xs border border-gray-200 bg-black w-2 h-2 mr-1">
                      </div>
                    </template>
                    <div class="leading-3">{{ port.state }}</div>
                  </span>
                </div>
                <div class="text-xs text-gray-200">
                  {{ port.protocol }}
                  <template v-if="port.banner">
                    <span class="text-gray-600 mx-1">|</span>
                    <span class="truncate block">
                      {{ port.banner }}
                    </span>
                  </template>
                  <template v-else-if="port.product">
                    <span class="text-gray-600 mx-1">|</span>
                    <span class="truncate block">
                      {{ port.product }}
                    </span>
                  </template>
                </div>
              </li>
            </ul>
          </div>
          <span v-if="criticalPorts.length == 0"
                class="italic text-gray-300">
            No critical port information available.
          </span>
          <div class="" v-if="otherPorts.length > 0">
            <span class="uppercase text-gray-300 text-xs font-semibold tracking-widest"
                  v-tooltip="
                'There are additional open ports that have no additional information.'
              "
            >
              [{{ otherPorts.length }}] additional {{ 'port' | pluralize(otherPorts.length) }} open
            </span>
          </div>
        </div>
        <div class="lg:w-1/4 border-b lg:border-b-0 border-gray-600 p-2 ">
          <div class="flex flex-col mb-2">
            <span class="text-lg">
              {{ 'Technology' | pluralize(technologies.length) }} [{{ technologies.length }}]
            </span>
          </div>
          <ul v-if="technologies.length > 0">
            <li v-for="(technology, index) in technologies"
                class="flex"
                :key="`${ip}-tech-${index}`">
              <template v-if="technology.classification === 'favorable'">
                <FavorableIcon v-tooltip="getVersionTooltip(technology.isUpToDate)"
                  class="mr-1" />
              </template>
              <template v-if="technology.classification === 'unfavorable'">
                <WarningIcon v-tooltip="getVersionTooltip(technology.isUpToDate)"
                  class="mr-1" />
              </template>
              {{ technology.description }}
              <span
                v-if="technology.semanticVersion"
                v-tooltip="getVersionTooltip(technology.isUpToDate)"
                :class="getVersionClass(technology.isUpToDate)"
                class="text-gray-300 text-xs font-semibold
                cursor-default inline-block ml-1 leading-6">
                  v{{ technology.semanticVersion }}
              </span>
            </li>
          </ul>
          <p class="italic text-gray-300 leading-5"
             v-else>
            No relevant technologies found on this host.
          </p>
        </div>
      </div>
      <div class="raw-data p-2 m-2" v-if="isSuperUser && adminViewActive">
      <Button type="text-small"
              class="py-2 text-gray-300 pl-2"
              @onClick="showRawData = !showRawData">
              {{ showOrHideByValue(showRawData) }}
              Raw Data [{{ renderCorrectExpandElement(showRawData) }}]
            </Button>
            <RawJSON
              v-if='showRawData'
              :json="rawData" />
      </div>
    </template>
    <div class="border-t border-gray-600 ">
      <Button type="text-small"
              class="w-full py-2 text-gray-300"
              @onClick="expanded = !expanded">
        {{ expanded ? 'Collapse' : 'Expand' }}
        <template v-slot:iconRight>
          <Chevron class="text-gray-200 transform
                            transition-transform duration-200 linear"
                   :class="{'-rotate-180': expanded}"/>
        </template>
      </Button>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

import Button from '@/components/Button.vue';
import Checkbox from '@/components/Checkbox.vue';
import Chevron from '@/assets/chevron.svg?inline';
import CopySubdomain from '@/components/Host/CopySubdomain.vue';
import FavorableIcon from '@/assets/favorable.svg?inline';
import RawJSON from '@/components/Admin/RawJSON.vue';
import WarningIcon from '@/assets/warning.svg?inline';

export default {
  name: 'Host',
  components: {
    Button,
    Checkbox,
    Chevron,
    CopySubdomain,
    FavorableIcon,
    RawJSON,
    WarningIcon,
  },
  methods: {
    renderCorrectExpandElement(value) {
      return value ? '-' : '+';
    },
    showOrHideByValue(value) {
      return value ? 'Hide' : 'Show';
    },
    getVersionTooltip(version) {
      if (version === null) return '';
      if (version) return 'Up to date';
      return 'Out of date';
    },
    getVersionClass(version) {
      if (version === null) return 'text-gray-300';
      if (version) return 'text-green-500';
      return 'text-yellow-500';
    },
    portIdentityKey(port) {
      return [
        port.portid || '',
        port.protocol || '',
        port.state || '',
        port.product || '',
        port.banner || '',
      ].join('|');
    },
    uniqueScreenshots(screenshots) {
      const seen = new Set();
      return screenshots.filter((screenshot) => {
        const key = screenshot.file
          || `${screenshot.ipAddress || ''}|${screenshot.port || ''}|${screenshot.domainName || ''}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    },
    portScreenshots(port) {
      if (Array.isArray(port.screenshots) && port.screenshots.length) {
        return this.uniqueScreenshots(port.screenshots);
      }

      if (port.screenshot) {
        return [{
          file: port.screenshot,
          ipAddress: this.ip,
          port: port.portid,
        }];
      }

      return [];
    },
    primaryPortScreenshot(port) {
      return this.portScreenshots(port)[0] || null;
    },
    additionalTargetScreenshotCount(target) {
      return Math.max(target.screenshots.length - 1, 0);
    },
    showTargetScreenshots(target) {
      this.$emit('showPortScreenshots', {
        screenshots: target.screenshots,
        hostIp: this.ip,
        targetLabel: target.label,
      });
    },
    portScreenshotLabel(screenshot) {
      return screenshot.domainName || screenshot.ipAddress || 'IP';
    },
    portScreenshotAltText(screenshot, port) {
      return `Screenshot of ${this.portScreenshotLabel(screenshot)}:${screenshot.port || port.portid}`;
    },
    screenshotTargetAltText(target) {
      return `Screenshot of ${target.label}`;
    },
  },
  props: {
    /*
     The IP of the host
     */
    ip: {
      type: String,
      required: true,
    },
    /*
     Whether the Host is External or Internal
     */
    category: {
      type: String,
      required: true,
    },
    /*
    Explanation of the categorization if available
    */
    categoryExplanation: {
      type: String,
      required: false,
    },
    /*
     The country the host is in
     */
    country: {
      type: String,
      required: true,
    },
    /*
     The ASN of the host
     */
    asn: {
      type: [String, Number],
      required: true,
    },
    /*
     The Organization the host belongs to
     */
    org: {
      type: String,
      required: true,
    },
    /*
     The findings for the host
     */
    findings: {
      type: Array,
      required: true,
    },
    /*
     An array of subdomains found for the host
     */
    subdomains: {
      type: Array,
      required: true,
    },
    /*
     An array of technology objects
     */
    technologies: {
      type: Array,
      required: true,
    },
    /*
     An array of port objects
     */
    ports: {
      type: Array,
      required: true,
    },
    /*
     Overrides state of expanded so all hosts can be collapsed / expanded on report
     */
    forceExpand: {
      type: Boolean || null,
    },
    highlighted: {
      type: Boolean,
      default: false,
    },
    /*
     Determines if superuser features are available
     */
    isSuperuser: {
      type: Boolean || null,
    },
    /*
     An array of screenshots from subdomains
     */
    screenshots: {
      type: Array,
      required: true,
    },
    /*
     The root domain (used to create list of subdomains to target for directory bruteforcing)
     */
    rootDomain: {
      type: String,
    },
    targetsForDictEnum: {
      type: Array,
    },
    rawData: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      expanded: false,
      showRawData: false,
      filteredSubdomains: [],
      showTooltip: false,
    };
  },
  mounted() {
    if (this.forceExpand !== null) {
      this.expanded = this.forceExpand;
    }
  },
  computed: {
    ...mapGetters(['isSuperUser', 'adminViewActive']),
    findingsCount() {
      return this.findings.length || 0;
    },
    displayPorts() {
      const portMap = new Map();

      this.ports.forEach((port) => {
        const key = this.portIdentityKey(port);
        if (!portMap.has(key)) {
          portMap.set(key, {
            ...port,
            screenshots: [...this.portScreenshots(port)],
          });
          return;
        }

        const existing = portMap.get(key);
        existing.screenshots = this.uniqueScreenshots([
          ...this.portScreenshots(existing),
          ...this.portScreenshots(port),
        ]);
        existing.screenshot = existing.screenshot || port.screenshot;
      });

      return [...portMap.values()];
    },
    screenshotTargets() {
      const targetMap = new Map();

      this.displayPorts.forEach((port) => {
        this.portScreenshots(port).forEach((screenshot) => {
          const label = this.portScreenshotLabel(screenshot);
          const key = label;
          if (!targetMap.has(key)) {
            targetMap.set(key, {
              label,
              primaryScreenshot: screenshot,
              screenshots: [],
              ports: [],
              protocol: port.protocol,
              state: port.state,
              product: port.product,
              banner: port.banner,
            });
          }

          const target = targetMap.get(key);
          target.screenshots = this.uniqueScreenshots([...target.screenshots, screenshot]);
          const portId = screenshot.port || port.portid;
          if (portId && !target.ports.includes(String(portId))) {
            target.ports.push(String(portId));
          }
        });
      });

      return [...targetMap.values()]
        .map((target) => ({
          ...target,
          ports: target.ports.sort((left, right) => Number(left) - Number(right)),
        }))
        .sort((left, right) => left.label.localeCompare(right.label));
    },
    screenshotPorts() {
      return this.displayPorts.filter((p) => this.portScreenshots(p).length);
    },
    informationalPorts() {
      return this.displayPorts.filter((p) => (
        !this.portScreenshots(p).length && (p.banner || p.product)
      ));
    },
    criticalPorts() {
      return [...this.screenshotPorts, ...this.informationalPorts];
    },
    otherPorts() {
      return this.displayPorts.filter((p) => !(
        p.banner || p.product || this.portScreenshots(p).length
      ));
    },
  },
  watch: {
    forceExpand(newForceExpand) {
      if (newForceExpand !== null) {
        this.expanded = newForceExpand;
      }
    },
    highlighted(newHighlighted) {
      if (newHighlighted) {
        this.expanded = true;
      }
    },
  },
};
</script>

<style scoped>
.host--highlighted {
  @apply border-yellow-500 shadow-glowyellow;
}

.screenshot-target-text {
  display: block;
  width: 100%;
  text-align: left;
  border: 0;
  margin: 0;
  cursor: pointer;
}

.glow-block {
  @apply absolute left-0;
  top: 0rem;
  width: 2px;
  height: 100%;
  content: '';

  &.glow-block--yellow {
     @apply bg-yellow-500 shadow-glowyellow;
   }

  &.glow-block--green {
     @apply bg-green-500 shadow-glowgreen;
   }
}

.warning-strip {
  @apply absolute h-px w-full top-0 left-0;
  background-color: transparent;
  background-repeat: repeat-x;
  background-size: 100% 88px;
  background-position: 0% 0%;
  background-image: repeating-linear-gradient(-50deg,
  transparent,
  transparent 11px,
  rgba(249, 194, 46, 1) 11px,
  rgba(249, 194, 46, 1) 22px);
}

.yellow-block::after {
  @apply absolute left-0 bg-yellow-500;
  top: 0.35rem;
  width: 2px;
  height: .75rem;
  content: '';
}

.tooltip {
  position: absolute;
  left: 100%;
  top: 50%;
  transform: translateY(-50%);
  white-space: nowrap;
  background-color: #333;
  color: #fff;
  padding: 5px 10px;
  border-radius: 4px;
  margin-left: 5px;
  z-index: 10;
}
</style>
